import { Link2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  CONSTITUENT_COMPLAINT_DOCUMENT_ROLE_LABELS,
  linkConstituentComplaintDocument,
  listConstituentComplaintDocuments,
  unlinkConstituentComplaintDocument,
  type ConstituentComplaintDocumentRole,
} from "@/api/constituent_complaint_documents";
import { ComplaintSection } from "@/components/trust_suite/ComplaintSection";
import {
  useWorkflowDocumentLinks,
  WORKFLOW_DOCUMENT_LINK_HELPER_TEXT,
  WorkflowDocumentLinkButton,
  WorkflowDocumentLinkList,
  WorkflowDocumentLinkPickerDialog,
  WorkflowDocumentLinksBlocked,
} from "@/components/workflow_tracking/WorkflowDocumentLinks";
import { invalidateTrustSuitePipeline } from "@/lib/trustSuiteQueryKeys";

type ComplaintDocumentsProps = {
  municipalityId: number;
  complaintId: number;
  fileViewerEnabled: boolean;
  embedded?: boolean;
};

const TRUST_ROLE_OPTIONS = Object.entries(CONSTITUENT_COMPLAINT_DOCUMENT_ROLE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export function ComplaintDocuments({
  municipalityId,
  complaintId,
  fileViewerEnabled,
  embedded = false,
}: ComplaintDocumentsProps) {
  const queryClient = useQueryClient();
  const linksKey = ["constituent_complaint_documents", municipalityId, complaintId] as const;

  const links = useWorkflowDocumentLinks({
    municipalityId,
    linksQueryKey: linksKey,
    listLinks: () => listConstituentComplaintDocuments(municipalityId, complaintId),
    linkDocument: (documentId, role) =>
      linkConstituentComplaintDocument(municipalityId, complaintId, documentId, {
        role: role as ConstituentComplaintDocumentRole,
      }),
    unlinkDocument: (linkId) =>
      unlinkConstituentComplaintDocument(municipalityId, complaintId, linkId),
    defaultRole: "attachment",
    enabled: fileViewerEnabled,
    onInvalidate: () => invalidateTrustSuitePipeline(queryClient, municipalityId),
  });

  const picker = (
    <WorkflowDocumentLinkPickerDialog
      open={links.pickerOpen}
      onOpenChange={links.setPickerOpen}
      description="Choose a library document to attach to this concern."
      selectedDocumentId={links.selectedDocumentId}
      onSelectedDocumentIdChange={links.setSelectedDocumentId}
      selectedRole={links.selectedRole}
      onSelectedRoleChange={links.setSelectedRole}
      roleOptions={TRUST_ROLE_OPTIONS}
      documentSearch={links.documentSearch}
      onDocumentSearchChange={links.setDocumentSearch}
      searchResults={links.searchResults}
      searchLoading={links.searchLoading}
      searchReady={links.searchReady}
      linking={links.linkMutation.isPending}
      onLink={() =>
        links.linkMutation.mutate({
          documentId: Number(links.selectedDocumentId),
          role: links.selectedRole,
        })
      }
    />
  );

  const list = (
    <WorkflowDocumentLinkList
      links={links.links}
      isLoading={links.isLoading}
      error={links.error}
      roleLabels={CONSTITUENT_COMPLAINT_DOCUMENT_ROLE_LABELS}
      emptyMessage="No documents linked yet. Attach files from your File Center."
      unlinking={links.unlinkMutation.isPending}
      onUnlink={(linkId) => links.unlinkMutation.mutate(linkId)}
    />
  );

  if (!fileViewerEnabled) {
    const blocked = (
      <WorkflowDocumentLinksBlocked
        testId="trust-complaint-documents"
        message="File Center access is required to attach documents to this concern."
      />
    );
    if (embedded) return blocked;
    return (
      <ComplaintSection title="Documents" description="Attach supporting files from File Center.">
        {blocked}
      </ComplaintSection>
    );
  }

  if (embedded) {
    return (
      <div className="min-w-0 space-y-4" data-testid="trust-complaint-documents">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 text-sm text-muted-foreground">
            {WORKFLOW_DOCUMENT_LINK_HELPER_TEXT}
          </p>
          <WorkflowDocumentLinkButton className="shrink-0" onClick={links.openPicker} />
        </div>
        {list}
        {picker}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="trust-complaint-documents">
      <ComplaintSection
        title="Documents"
        description={WORKFLOW_DOCUMENT_LINK_HELPER_TEXT}
        action={
          links.links.length > 0 ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Link2 className="h-3.5 w-3.5" />
                {links.links.length} linked
              </span>
              <WorkflowDocumentLinkButton onClick={links.openPicker} />
            </div>
          ) : (
            <WorkflowDocumentLinkButton onClick={links.openPicker} />
          )
        }
      >
        {list}
        {picker}
      </ComplaintSection>
    </div>
  );
}
